import { API_CONFIG, EMAIL_API_ENDPOINTS, STORAGE_KEYS } from '../helpers/constants';

const EMAIL_API_BASE_URL = API_CONFIG.EMAIL_API_BASE_URL;

const getAuthHeaders = (): HeadersInit => {
    const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || '';
    return {
        'accept': 'application/json',
        'Authorization': `Bearer ${token}`
    };
};

// Dashboard Metrics Interfaces

// 1. Throughput Metrics
export interface EmailDistribution {
    spam: { count: number; percentage: number };
    no_reply: { count: number; percentage: number };
    inbox: { count: number; percentage: number };
}

export interface Trend {
    direction: 'increase' | 'decrease' | 'stable';
    percentage_change: number;
    previous_period_count: number;
}

export interface ShayQueryUser {
    user_id: string;
    user_name: string;
    query_count: number;
}

export interface ShayQueries {
    total_count: number;
    top_users: ShayQueryUser[];
}

export interface ThroughputMetricsResponse {
    total_emails_synced: number;
    emails_synced_today: number;
    email_distribution: EmailDistribution;
    trend: Trend;
    shay_queries: ShayQueries;
    shay_queries_trend?: Trend;
    today_emails_trend?: Trend;
}

// 2. Strategic Insights
export interface TopicCloudItem {
    topic: string;
    volume: number;
    current_volume?: number;
    percentage?: number;
}

export interface EmergingTopic {
    topic: string;
    percentage_change: number;
    current_volume: number;
    previous_volume: number;
    direction: 'increase' | 'decrease' | 'stable';
}

export interface StrategicInsightsResponse {
    topic_cloud: TopicCloudItem[];
    emerging_topics: EmergingTopic[];
}

// 3. Business Outcome Metrics
export interface MTTR {
    value_minutes: number;
    value_seconds: number;
    trend?: Trend;
}

export interface ResponseRate {
    percentage: number;
    trend?: Trend;
}

export interface DraftToSentRatio {
    ratio: number;
    ratio_formatted: string;
    draft_utilization_percentage: number;
    total_drafts: number;
    drafts_sent: number;
    trend?: Trend;
}

export interface UserContributionUser {
    user_name: string;
    /** Prism7 / Shay query count for this user (optional from API) */
    prism7_queries?: number;
    drafts_viewed: number;
    drafts_edited: number;
    messages_sent: number;
    topics_addressed: string | number;
}

export interface UserContribution {
    users: UserContributionUser[];
}

export interface BusinessOutcomeMetricsResponse {
    mean_time_to_respond: MTTR;
    response_rate: ResponseRate;
    draft_to_sent_ratio: DraftToSentRatio;
    user_contribution: UserContribution;
}

// 4. Overview Metrics
export interface SyncStatus {
    status: string;
    is_connected: boolean;
}

export interface ActiveUsers {
    active_count: number;
    total_count: number;
    formatted: string;
}

export interface GlobalMTTR {
    value_hours: number;
    value_minutes: number;
    formatted: string;
    total_responses: number;
}

export interface OverviewMetricsResponse {
    sync_status: SyncStatus;
    active_users: ActiveUsers;
    global_mttr: GlobalMTTR;
    active_integrations: any[]; // Or more specific if known, e.g. AppIntegration[]
}

// 5. Tasks and Approvals
export interface TaskUser {
    user_id: string;
    user_name: string;
    task_count: number;
}

export interface TasksMetrics {
    total_open_tasks: number;
    tasks_by_user: TaskUser[];
}

export interface ApprovalUser {
    user_id: string;
    user_name: string;
    approval_count: number;
}

export interface ApprovalsMetrics {
    total_pending_approvals: number;
    approvals_by_user: ApprovalUser[];
}

export interface TasksAndApprovalsMetricsResponse {
    tasks: TasksMetrics;
    approvals: ApprovalsMetrics;
}

// 6. Timeline Graph
export interface TimelineDataPoint {
    timestamp: string;
    inbox_count: number;
    spam_count: number;
    no_reply_count: number;
    total_count: number;
}

export interface TimelineMetricsResponse {
    timeline_data: TimelineDataPoint[];
    bucket_size: string;
    period?: {
        start_date: string;
        end_date: string;
    };
}

// 7. App Usage Analytics
export interface CategoryUsage {
    category: string;
    app_count: number;
    total_invocations: number;
    total_invocations_percentage: number;
}

export interface AppToolUsage {
    app_name: string;
    tool_name: string;
    invocation_count: number;
}

export interface AppUsageMetricsResponse {
    total_app_integrations?: number;
    /** From API: total_app_interactions – total AI-triggered app invocations */
    total_app_interactions: number;
    category_wise_usage: CategoryUsage[];
    app_tool_usage: AppToolUsage[];
}

// 8. Flow Run Metrics (Business Rules Workflow execution counts)
export interface FlowRun {
    flow_id: string;
    flow_name: string;
    run_count: number;
    completed_count: number;
    failed_count: number;
    approval_requested_count: number;
    avg_duration_ms: number;
    last_run_at: string | null;
    is_current: boolean;
}

export interface FlowRunMetricsResponse {
    channel_id: string;
    time_filter: string;
    period_start: string;
    period_end: string;
    flow_runs: FlowRun[];
}

// Service Functions
export const getThroughputMetrics = async (
    channelId: string,
    timeFilter: string,
    startDate?: string,
    endDate?: string
): Promise<ThroughputMetricsResponse> => {
    try {
        const queryParams = new URLSearchParams({
            channel_id: channelId,
            time_filter: timeFilter,
        });
        if (startDate) queryParams.append('start_date', startDate);
        if (endDate) queryParams.append('end_date', endDate);

        const response = await fetch(`${EMAIL_API_BASE_URL}${EMAIL_API_ENDPOINTS.GET_THROUGHPUT_METRICS}?${queryParams}`, {
            method: 'GET',
            headers: getAuthHeaders()
        });

        if (!response.ok) throw new Error('Failed to fetch throughput metrics');
        return await response.json();
    } catch (error) {
        console.error('Get throughput metrics error:', error);
        throw error;
    }
};

export const getStrategicInsights = async (
    channelId: string,
    timeFilter: string,
    startDate?: string,
    endDate?: string
): Promise<StrategicInsightsResponse> => {
    try {
        const queryParams = new URLSearchParams({
            channel_id: channelId,
            time_filter: timeFilter,
        });
        if (startDate) queryParams.append('start_date', startDate);
        if (endDate) queryParams.append('end_date', endDate);

        const response = await fetch(`${EMAIL_API_BASE_URL}${EMAIL_API_ENDPOINTS.GET_STRATEGIC_INSIGHTS}?${queryParams}`, {
            method: 'GET',
            headers: getAuthHeaders()
        });

        if (!response.ok) throw new Error('Failed to fetch strategic insights');
        return await response.json();
    } catch (error) {
        console.error('Get strategic insights error:', error);
        throw error;
    }
};

export const getBusinessOutcomeMetrics = async (
    channelId: string,
    timeFilter: string,
    startDate?: string,
    endDate?: string,
    userContributionSort?: string
): Promise<BusinessOutcomeMetricsResponse> => {
    try {
        const queryParams = new URLSearchParams({
            channel_id: channelId,
            time_filter: timeFilter,
        });
        if (startDate) queryParams.append('start_date', startDate);
        if (endDate) queryParams.append('end_date', endDate);
        if (userContributionSort) queryParams.append('user_contribution_sort', userContributionSort);


        const response = await fetch(`${EMAIL_API_BASE_URL}${EMAIL_API_ENDPOINTS.GET_BUSINESS_OUTCOME_METRICS}?${queryParams}`, {
            method: 'GET',
            headers: getAuthHeaders()
        });

        if (!response.ok) throw new Error('Failed to fetch business outcome metrics');
        return await response.json();
    } catch (error) {
        console.error('Get business outcome metrics error:', error);
        throw error;
    }
};

export const getOverviewMetrics = async (
    channelId: string
): Promise<OverviewMetricsResponse> => {
    try {
        const response = await fetch(`${EMAIL_API_BASE_URL}${EMAIL_API_ENDPOINTS.GET_OVERVIEW_METRICS}?channel_id=${channelId}`, {
            method: 'GET',
            headers: getAuthHeaders()
        });
        
        if (!response.ok) throw new Error('Failed to fetch overview metrics');
        return await response.json();
    } catch (error) {
        console.error('Get overview metrics error:', error);
        throw error;
    }
};

export const getTasksAndApprovalsMetrics = async (
    channelId: string,
    timeFilter: string,
    startDate?: string,
    endDate?: string
): Promise<TasksAndApprovalsMetricsResponse> => {
    try {
        const queryParams = new URLSearchParams({
            channel_id: channelId,
            time_filter: timeFilter,
        });
        if (startDate) queryParams.append('start_date', startDate);
        if (endDate) queryParams.append('end_date', endDate);

        const response = await fetch(`${EMAIL_API_BASE_URL}${EMAIL_API_ENDPOINTS.GET_TASKS_AND_APPROVALS_METRICS}?${queryParams}`, {
             method: 'GET',
            headers: getAuthHeaders()
        });

        if (!response.ok) throw new Error('Failed to fetch tasks and approvals metrics');
        return await response.json();
    } catch (error) {
        console.error('Get tasks and approvals metrics error:', error);
        throw error;
    }
};

export const getTimelineMetrics = async (
    channelId: string,
    timeFilter: string,
    startDate?: string,
    endDate?: string
): Promise<TimelineMetricsResponse> => {
    try {
        const queryParams = new URLSearchParams({
            channel_id: channelId,
            time_filter: timeFilter,
        });
        if (startDate) queryParams.append('start_date', startDate);
        if (endDate) queryParams.append('end_date', endDate);

        const response = await fetch(`${EMAIL_API_BASE_URL}${EMAIL_API_ENDPOINTS.GET_TIMELINE_METRICS}?${queryParams}`, {
             method: 'GET',
            headers: getAuthHeaders()
        });

        if (!response.ok) throw new Error('Failed to fetch timeline metrics');
        return await response.json();
    } catch (error) {
        console.error('Get timeline metrics error:', error);
        throw error;
    }
};

export const getAppUsageMetrics = async (
    channelId: string,
    timeFilter: string,
    startDate?: string,
    endDate?: string
): Promise<AppUsageMetricsResponse> => {
    try {
        const queryParams = new URLSearchParams({
            channel_id: channelId,
            time_filter: timeFilter,
        });
        if (startDate) queryParams.append('start_date', startDate);
        if (endDate) queryParams.append('end_date', endDate);

        const response = await fetch(`${EMAIL_API_BASE_URL}${EMAIL_API_ENDPOINTS.GET_APP_USAGE_METRICS}?${queryParams}`, {
             method: 'GET',
            headers: getAuthHeaders()
        });

        if (!response.ok) throw new Error('Failed to fetch app usage metrics');
        return await response.json();
    } catch (error) {
        console.error('Get app usage metrics error:', error);
        throw error;
    }
};

export const getFlowRunMetrics = async (
    channelId: string,
    timeFilter: string,
    startDate?: string,
    endDate?: string
): Promise<FlowRunMetricsResponse> => {
    try {
        const queryParams = new URLSearchParams({
            time_filter: timeFilter,
        });
        if (startDate) queryParams.append('start_date', startDate);
        if (endDate) queryParams.append('end_date', endDate);

        const response = await fetch(`${EMAIL_API_BASE_URL}${EMAIL_API_ENDPOINTS.GET_FLOW_RUN_METRICS(channelId)}?${queryParams}`, {
            method: 'GET',
            headers: getAuthHeaders()
        });

        if (!response.ok) throw new Error('Failed to fetch flow run metrics');
        return await response.json();
    } catch (error) {
        console.error('Get flow run metrics error:', error);
        throw error;
    }
};
